/** Client-side aggregation for the batch tenant-config grid.
 *
 * All ~200 tenants' configs arrive in one response; everything here is pure
 * in-memory aggregation: one flatten pass builds an inverted index
 * (canonical key → per-tenant value occurrences), then the served schema
 * bundle and the index merge into the grid's row list.
 */
import type { ConfigSchemaBundle, TenantConfigItem } from '@/api/tourOperatorConfigs';

import { formatType } from '../config/schema-grid/schemaOps';
import type { SchemaProp } from '../config/schema-grid/types';

import { flattenConfigLeaves, flattenSchemaLeaves, type SchemaLeaf } from './pathOps';

export interface ValueOccurrence {
  tenantId: number;
  concretePath: string;
  value: unknown;
}

export interface ConfigIndex {
  /** Canonical key → every (tenant, concrete path, value) occurrence. */
  byKey: Map<string, ValueOccurrence[]>;
  /** Every canonical key observed in at least one tenant's config. */
  observedKeys: Set<string>;
}

/** Build the inverted index in ONE flatten pass over all tenants. */
export function buildConfigIndex(tenants: TenantConfigItem[]): ConfigIndex {
  const byKey = new Map<string, ValueOccurrence[]>();
  for (const tenant of tenants) {
    if (!tenant.config) continue;
    for (const leaf of flattenConfigLeaves(tenant.config)) {
      let occurrences = byKey.get(leaf.canonicalKey);
      if (!occurrences) {
        occurrences = [];
        byKey.set(leaf.canonicalKey, occurrences);
      }
      occurrences.push({
        tenantId: tenant.id,
        concretePath: leaf.concretePath,
        value: leaf.value,
      });
    }
  }
  return { byKey, observedKeys: new Set(byKey.keys()) };
}

export interface BatchRow {
  /** Canonical key — doubles as the DataGrid row id. */
  id: string;
  key: string;
  /** 'core' for the top-level schema, else the restech name. */
  group: string;
  schema: SchemaProp | null;
  type: string;
  secret: boolean;
  widget: string | null;
  description: string;
  /** Distinct tenants with at least one occurrence of the key. */
  definedCount: number;
  /** Distinct value groups among occurrences (1/0 for secrets — the masked
   *  sentinel makes value distinctions meaningless). */
  distinctCount: number;
}

function inferType(value: unknown): string {
  if (value === null) return 'null';
  if (Array.isArray(value)) return 'array';
  const t = typeof value;
  if (t === 'boolean' || t === 'number' || t === 'string' || t === 'object') return t;
  return 'string'; // non-JSON values cannot occur in a parsed config
}

function countsFor(
  occurrences: ValueOccurrence[],
  secret: boolean,
): { definedCount: number; distinctCount: number } {
  const definedCount = new Set(occurrences.map((o) => o.tenantId)).size;
  if (secret) return { definedCount, distinctCount: occurrences.length > 0 ? 1 : 0 };
  const distinctCount = new Set(occurrences.map((o) => JSON.stringify(o.value))).size;
  return { definedCount, distinctCount };
}

/** First key segment of a canonical key ("ventrata.source_id" → "ventrata"). */
function firstKeySegment(canonicalKey: string): string {
  const dot = canonicalKey.indexOf('.');
  const bracket = canonicalKey.indexOf('[');
  const ends = [dot, bracket].filter((i) => i !== -1);
  return ends.length === 0 ? canonicalKey : canonicalKey.slice(0, Math.min(...ends));
}

/** Merge the served schema bundle with the observed-config index into the
 *  grid's rows. Sources, in order:
 *
 *  1. top-schema leaves (group 'core');
 *  2. per-restech integration-schema leaves, keys prefixed `${restech}.`;
 *  3. observed-only canonical keys not covered by 1/2 — rendered as
 *     "unknown" rows (schema null, type inferred from the first occurrence,
 *     group inferred from the first key segment when it names a restech).
 *
 *  A schema ARRAY leaf without `items.properties` whose observed data holds
 *  object elements ALSO emits the observed `key[*].child` rows (schema null,
 *  description and group inherited from the parent).
 *
 *  `_tenantCount` is accepted for the page layer's "defined in X of N"
 *  display contract but is not needed by any row field yet. */
export function buildBatchRows(
  bundle: ConfigSchemaBundle,
  index: ConfigIndex,
  _tenantCount: number,
): BatchRow[] {
  const rows = new Map<string, BatchRow>();

  const schemaLeaves: SchemaLeaf[] = [...flattenSchemaLeaves(bundle.top.properties ?? {}, 'core')];
  for (const [restech, schema] of Object.entries(bundle.integrations)) {
    for (const leaf of flattenSchemaLeaves(schema.properties ?? {}, restech)) {
      schemaLeaves.push({ ...leaf, key: `${restech}.${leaf.key}` });
    }
  }

  // 1 + 2 — schema-defined rows.
  for (const leaf of schemaLeaves) {
    const occurrences = index.byKey.get(leaf.key) ?? [];
    const secret = leaf.schema['x-secret'] === true;
    const { definedCount, distinctCount } = countsFor(occurrences, secret);
    rows.set(leaf.key, {
      id: leaf.key,
      key: leaf.key,
      group: leaf.group,
      schema: leaf.schema,
      type: formatType(leaf.schema),
      secret,
      widget: leaf.schema['x-widget'] ?? null,
      description: leaf.schema.description ?? '',
      definedCount,
      distinctCount,
    });
  }

  // Observed [*] children under schema array leaves without items.properties.
  for (const leaf of schemaLeaves) {
    if (leaf.schema.type !== 'array') continue;
    const itemProps = leaf.schema.items?.properties;
    if (itemProps && Object.keys(itemProps).length > 0) continue;
    const prefix = `${leaf.key}[*]`;
    for (const observed of index.observedKeys) {
      if (!observed.startsWith(prefix) || rows.has(observed)) continue;
      const occurrences = index.byKey.get(observed) ?? [];
      const first = occurrences[0];
      const { definedCount, distinctCount } = countsFor(occurrences, false);
      rows.set(observed, {
        id: observed,
        key: observed,
        group: leaf.group,
        schema: null,
        type: first === undefined ? 'unknown' : inferType(first.value),
        secret: false,
        widget: null,
        description: leaf.schema.description ?? '',
        definedCount,
        distinctCount,
      });
    }
  }

  // 3 — observed-only keys not covered by any schema leaf.
  const integrationNames = new Set(Object.keys(bundle.integrations));
  for (const observed of index.observedKeys) {
    if (rows.has(observed)) continue;
    const occurrences = index.byKey.get(observed) ?? [];
    const first = occurrences[0];
    const head = firstKeySegment(observed);
    const { definedCount, distinctCount } = countsFor(occurrences, false);
    rows.set(observed, {
      id: observed,
      key: observed,
      group: integrationNames.has(head) ? head : 'core',
      schema: null,
      type: first === undefined ? 'unknown' : inferType(first.value),
      secret: false,
      widget: null,
      description: '',
      definedCount,
      distinctCount,
    });
  }

  return [...rows.values()].sort((a, b) => {
    if (a.group !== b.group) {
      if (a.group === 'core') return -1;
      if (b.group === 'core') return 1;
      return a.group.localeCompare(b.group);
    }
    return a.key.localeCompare(b.key);
  });
}
